// DevCockpitPage.js — Cockpit développeur ITRIIA (phase 3)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Terminal, Code2, FolderGit2,
    Loader2, AlertTriangle, Cpu, CheckCircle2, XCircle, Play,
} from 'lucide-react';
import {
    getStatus, getRunners, createProbeRun, createVersionRun, streamRun, DevCockpitApiError,
} from '../../services/devCockpitApi';
import DevWorkspaceLayout from './DevWorkspaceLayout';
import './devCockpit.css';

const RUNNER_ICONS = {
    'cursor-sdk': Cpu,
    'claude-code': Code2,
    'codex': Terminal,
};

function RunnerCard({ runner, probing, onProbe, onVersion }) {
    const Icon = RUNNER_ICONS[runner.id] || Cpu;
    const available = runner.available === true;
    const statusColor = available ? '#34d399' : '#fbbf24';
    const StatusIcon = available ? CheckCircle2 : XCircle;

    return (
        <div className={`dev-card${available ? ' dev-card--ok' : ' dev-card--warn'}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Icon size={18} style={{ color: 'var(--pill-soft-icon)' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{runner.label}</span>
                <StatusIcon size={14} color={statusColor} style={{ marginLeft: 'auto' }} />
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {runner.souverain ? 'BYOK · souverain' : 'Cloud'}
                <span style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                    {available ? 'Binaire ou clé détecté' : 'Non installé sur ce serveur'}
                </span>
                {runner.detail && (
                    <span style={{ display: 'block', marginTop: 4, fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
                        {runner.detail}
                    </span>
                )}
                {!available && runner.hint && (
                    <span style={{ display: 'block', marginTop: 4, color: '#fbbf24' }}>{runner.hint}</span>
                )}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" onClick={() => onProbe(runner.id)} disabled={probing} title="Teste API + DB + SSE" className="dev-btn">
                    {probing ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={12} />}
                    Probe infra
                </button>
                <button type="button" onClick={() => onVersion(runner.id)} disabled={probing || !available} title={available ? 'Spawn --version' : 'Runner requis'} className="dev-btn dev-btn--primary">
                    Test --version
                </button>
            </div>
        </div>
    );
}

function RunnersPanel({ runners, probingId, timeline, onProbe, onVersion }) {
    return (
        <>
            <div className="dev-card" style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text)' }}>Runner Registry.</strong>{' '}
                <strong>Probe infra</strong> = plomberie (API, DB, SSE).
                <strong> Test --version</strong> = spawn réel (si détecté).
            </div>
            <div className="dev-runners-grid" style={{ marginBottom: 20 }}>
                {runners.map((r) => (
                    <RunnerCard key={r.id} runner={r} probing={probingId === r.id} onProbe={onProbe} onVersion={onVersion} />
                ))}
            </div>
            {timeline.length > 0 && (
                <div className="dev-card">
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>Timeline</div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, lineHeight: 1.7, color: 'var(--text-secondary)', maxHeight: 180, overflow: 'auto' }}>
                        {timeline.map((ev) => (
                            <div key={ev.id}>
                                <span style={{ color: 'var(--pill-soft-text)' }}>[{ev.type}]</span>{' '}
                                {ev.message || ev.detail || JSON.stringify(ev)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

export default function DevCockpitPage() {
    const [tab, setTab] = useState('workspace');
    const [status, setStatus] = useState(null);
    const [runners, setRunners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [probingId, setProbingId] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const streamRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [st, rn] = await Promise.all([getStatus(), getRunners()]);
            setStatus(st);
            setRunners(rn.runners || []);
        } catch (e) {
            const msg = e instanceof DevCockpitApiError
                ? e.message
                : (e.name === 'AbortError' ? 'Délai dépassé' : 'Erreur réseau');
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        return () => streamRef.current?.close();
    }, [load]);

    const startStream = async (runnerId, createFn) => {
        streamRef.current?.close();
        setProbingId(runnerId);
        setTimeline([]);
        try {
            const { run_id: runId } = await createFn(runnerId);
            streamRef.current = streamRun(runId, {
                onEvent: (type, data) => {
                    setTimeline((prev) => [...prev, { type, ...data, id: `${type}-${prev.length}` }]);
                },
                onError: (err) => {
                    setTimeline((prev) => [...prev, { type: 'error', message: err.message, id: 'err' }]);
                    setProbingId(null);
                },
                onDone: () => {
                    setProbingId(null);
                    load();
                },
            });
        } catch (e) {
            setTimeline([{ type: 'error', message: e.message, id: 'err' }]);
            setProbingId(null);
        }
    };

    const phase = status?.phase ?? 4;

    const tabBtn = (id, label, Icon) => (
        <button
            type="button"
            onClick={() => setTab(id)}
            className={`dev-cockpit__tab${tab === id ? ' dev-cockpit__tab--active' : ''}`}
        >
            <Icon size={14} />
            {label}
        </button>
    );

    return (
        <div className="dev-cockpit">
            <div className="dev-cockpit__hero">
                <div className="dev-cockpit__icon">
                    <Code2 size={20} style={{ color: 'var(--pill-soft-icon)' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <h1 className="dev-cockpit__title">ITRIIA DEV</h1>
                    <p className="dev-cockpit__subtitle">Cockpit développeur — Workspace + Runners BYOK</p>
                </div>
                {status && <span className="dev-cockpit__phase">Phase {phase}</span>}
            </div>

            <div className="dev-cockpit__nav">
                {tabBtn('workspace', 'Workspace', FolderGit2)}
                {tabBtn('runners', 'Runners', Terminal)}
            </div>

            <div className="dev-cockpit__body">
                {loading && (
                    <div className="dev-card" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-secondary)' }}>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Connexion au module…
                    </div>
                )}

                {error && (
                    <div className="dev-card" style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', gap: 10, color: '#f87171' }}>
                        <AlertTriangle size={16} />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && tab === 'workspace' && <DevWorkspaceLayout />}

                {!loading && tab === 'runners' && status?.enabled && (
                    <div style={{ maxWidth: 960, overflow: 'auto' }}>
                        <RunnersPanel
                            runners={runners}
                            probingId={probingId}
                            timeline={timeline}
                            onProbe={(id) => startStream(id, createProbeRun)}
                            onVersion={(id) => startStream(id, createVersionRun)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
